import { UserModel } from '../models/user.model'
import { DateTools } from './date-tools'

/**
 * Classe utilitaire pour gérer les itinéraires et les adresses
 */
export class RouteTools {
  /**
   * Génère une URL Google Maps pour l'itinéraire journalier d'un technicien
   * @param technician Le technicien pour lequel générer l'itinéraire
   * @param date La date de l'itinéraire
   * @param events Tous les événements disponibles
   * @returns L'URL de l'itinéraire Google Maps
   */
  public static generateDailyRouteUrl(
    technician: UserModel,
    date: Date | string,
    allEvents: any[]
  ): string {
    if (!technician || !date || !allEvents || allEvents.length === 0) {
      console.error("Données manquantes pour générer l'URL d'itinéraire")
      return ''
    }

    // S'assurer que la date est au format Date
    const eventDate = typeof date === 'string' ? new Date(date) : new Date(date)

    // Formater la date au format YYYY-MM-DD pour la comparaison
    const dateString = DateTools.formatDateForAPI(eventDate)

    // Filtrer les événements pour ce technicien à cette date
    const technicianEvents = this.filterEventsForTechnician(
      technician,
      dateString,
      allEvents
    )

    if (technicianEvents.length === 0) {
      console.warn('Aucun événement trouvé pour ce technicien à cette date')
      return ''
    }

    // Trier les événements par heure de début
    technicianEvents.sort((a, b) => {
      const aStart = new Date(a.start).getTime()
      const bStart = new Date(b.start).getTime()
      return aStart - bStart
    })

    // Construire l'URL Google Maps
    return this.buildGoogleMapsUrl(technician, technicianEvents)
  }

  /**
   * Vérifie si un technicien a des interventions pour une période donnée
   * @param technician Le technicien à vérifier
   * @param allEvents Tous les événements disponibles
   * @returns true si le technicien a des interventions, false sinon
   */
  public static technicianHasInterventions(
    technician: UserModel,
    allEvents: any[]
  ): boolean {
    const events = allEvents.filter((event) => {
      // Vérifier que c'est une intervention
      if (!event.data || event.data.resource !== 'intervention') return false

      // Vérifier si le technicien est assigné à cette intervention
      return event.data.usersTechnicians?.some(
        (tech: any) => tech.email === technician.email
      )
    })

    return events.length > 0
  }

  /**
   * Filtre les événements pour un technicien à une date spécifique
   * @private
   */
  private static filterEventsForTechnician(
    technician: UserModel,
    dateString: string,
    allEvents: any[]
  ): any[] {
    return allEvents.filter((event) => {
      // Vérifier si c'est une intervention
      if (!event.data || event.data.resource !== 'intervention') {
        return false
      }

      // Vérifier si le technicien est assigné à cette intervention
      const hasTechnician = event.data.usersTechnicians?.some(
        (tech: any) => tech.email === technician.email
      )

      if (!hasTechnician) {
        return false
      }

      // Vérifier si l'intervention est à la même date
      const eventStartDate =
        typeof event.start === 'string'
          ? new Date(event.start)
          : new Date(event.start)

      const eventDateString = DateTools.formatDateForAPI(eventStartDate)
      return eventDateString === dateString
    })
  }

  /**
   * Construit l'URL de Google Maps avec le domicile comme point de départ/arrivée
   * et les interventions comme waypoints
   * @private
   */
  private static buildGoogleMapsUrl(
    technician: UserModel,
    events: any[]
  ): string {
    let mapsUrl = 'https://www.google.com/maps/dir/?api=1'

    // Formater l'adresse du domicile du technicien
    const technicianAddress = this.formatTechnicianAddress(technician)

    // Utiliser le domicile du technicien comme origine
    mapsUrl += `&origin=${technicianAddress}`

    // Utiliser le même domicile comme destination finale
    mapsUrl += `&destination=${technicianAddress}`

    // Ajouter toutes les interventions comme waypoints
    if (events.length > 0) {
      mapsUrl += '&waypoints='
      const waypoints = events
        .map((event) => this.formatInterventionAddress(event))
        .join('|')
      mapsUrl += waypoints
    }

    // Ajouter le mode de transport (driving par défaut)
    mapsUrl += '&travelmode=driving'

    return mapsUrl
  }

  /**
   * Formate et encode l'adresse d'une intervention pour l'URL
   * @private
   */
  private static formatInterventionAddress(event: any): string {
    const intervention = event.data
    const address = [
      intervention.workSitePostalCode || '',
      intervention.workSiteLocality || '',
    ]
      .filter(Boolean)
      .join(', ')

    return encodeURIComponent(address)
  }

  /**
   * Formate et encode l'adresse du domicile du technicien
   * @private
   */
  private static formatTechnicianAddress(technician: UserModel): string {
    const address = [technician.postalCode || '', technician.locality || '']
      .filter(Boolean)
      .join(', ')

    return encodeURIComponent(address)
  }

  /**
   * Vérifie si deux dates correspondent au même jour
   * @param date1 Première date
   * @param date2 Deuxième date
   * @returns true si les dates sont le même jour
   */
  static isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }
}
